import React, { useState, useCallback } from 'react';
import api from '../utils/axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  UserSearch, Loader2, FileText, FileSpreadsheet, TrendingUp, TrendingDown,
  ArrowRightLeft, AlertTriangle, ShieldAlert, Building2, HandCoins, Users, Clock,
  History, Eye, Trash2, RefreshCw,
} from 'lucide-react';

const fmt = (v) => {
  try { return `$ ${Number(v || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`; }
  catch { return '$ 0'; }
};
const fmtNum = (v) => Number(v || 0).toLocaleString('es-AR');

const hace12meses = () => {
  const h = new Date();
  const d = new Date(h.getFullYear() - 1, h.getMonth(), h.getDate());
  return d.toISOString().slice(0, 10);
};
const hoyISO = () => new Date().toISOString().slice(0, 10);

const nivelColor = (n) => ({
  Alto: { bg: '#fee2e2', fg: '#dc2626' },
  Medio: { bg: '#fef3c7', fg: '#d97706' },
  Bajo: { bg: '#dcfce7', fg: '#16a34a' },
}[n] || { bg: '#f1f5f9', fg: '#64748b' });

const señalColor = (n) => ({ alto: '#dc2626', medio: '#d97706', bajo: '#16a34a' }[n] || '#64748b');

const FRANJA_COLOR = { Madrugada: '#6366f1', 'Mañana': '#0ea5e9', Tarde: '#f59e0b', Noche: '#8b5cf6' };
const franjaDeHora = (h) => (h < 6 ? 'Madrugada' : h < 12 ? 'Mañana' : h < 18 ? 'Tarde' : 'Noche');

const KPI = ({ icon: Icon, label, value, sub, tone }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
    <div className="flex items-center gap-1.5 text-[11px] text-slate-500 uppercase tracking-wide"><Icon className="h-3.5 w-3.5" />{label}</div>
    <p className={`text-lg font-bold mt-1 ${tone || 'text-slate-800 dark:text-slate-100'}`}>{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const Contrapartes = ({ titulo, cps, apoc, vig, testid }) => (
  <div data-testid={testid}>
    <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-1.5">{titulo}</p>
    {(!cps || cps.length === 0) ? (
      <p className="text-xs text-slate-400">Sin operaciones en el período.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-slate-400 text-left border-b border-slate-100 dark:border-slate-800">
            <th className="py-1 pr-2">CUIT</th><th className="py-1 pr-2">Nombre</th>
            <th className="py-1 pr-2 text-right">Ops.</th><th className="py-1 pr-2 text-right">Monto</th><th className="py-1">Flag</th>
          </tr></thead>
          <tbody>
            {cps.slice(0, 10).map((c, i) => {
              const esApoc = apoc.includes(c.cuit); const esVig = vig.includes(c.cuit);
              return (
                <tr key={i} className="border-b border-slate-50 dark:border-slate-800/50">
                  <td className="py-1 pr-2 font-mono text-slate-600 dark:text-slate-300">{c.cuit_fmt || '—'}</td>
                  <td className="py-1 pr-2 text-slate-700 dark:text-slate-200 truncate max-w-[200px]">{c.nombre || 'Sin identificar'}</td>
                  <td className="py-1 pr-2 text-right tabular-nums text-slate-500">{fmtNum(c.operaciones)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums font-medium text-slate-700 dark:text-slate-200">{fmt(c.monto)}</td>
                  <td className="py-1">{esApoc ? <Badge className="bg-rose-100 text-rose-700 border-0 text-[9px]">APÓCRIFA</Badge> : esVig ? <Badge className="bg-amber-100 text-amber-700 border-0 text-[9px]">VIGILADA</Badge> : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const MiniBars = ({ serie }) => {
  if (!serie || serie.length === 0) return <p className="text-xs text-slate-400">Sin datos mensuales.</p>;
  const max = Math.max(...serie.map((m) => Math.max(m.ingreso, m.egreso)), 1);
  return (
    <div className="flex items-end gap-1.5 h-24 overflow-x-auto pb-1" data-testid="pt-serie">
      {serie.map((m, i) => (
        <div key={i} className="flex flex-col items-center gap-0.5 min-w-[26px]">
          <div className="flex items-end gap-0.5 h-20">
            <div title={`Ingresos ${fmt(m.ingreso)}`} style={{ height: `${(m.ingreso / max) * 100}%`, backgroundColor: '#16a34a' }} className="w-2 rounded-t pt-bar pt-bar-in" />
            <div title={`Egresos ${fmt(m.egreso)}`} style={{ height: `${(m.egreso / max) * 100}%`, backgroundColor: '#dc2626' }} className="w-2 rounded-t pt-bar pt-bar-out" />
          </div>
          <span className="text-[8px] text-slate-400 rotate-0">{m.mes.slice(2)}</span>
        </div>
      ))}
    </div>
  );
};

const HoraBars = ({ ah, cuit }) => {
  if (!ah || !ah.por_hora || !ah.por_hora.some((x) => x.operaciones > 0)) {
    return <p className="text-xs text-slate-400">Sin datos horarios en el período.</p>;
  }
  const max = Math.max(...ah.por_hora.map((x) => x.operaciones), 1);
  const pico = ah.franja_pico?.nombre;
  const hp = ah.hora_pico || {};
  return (
    <div data-testid={`pt-horas-${cuit}`}>
      <div className="flex items-end gap-[2px] h-20">
        {ah.por_hora.map((x) => {
          const fr = franjaDeHora(x.hora);
          return (
            <div key={x.hora} className="flex-1 flex flex-col items-center justify-end h-full" title={`${String(x.hora).padStart(2, '0')}:00 h · ${fmtNum(x.operaciones)} ops · ${fmt(x.monto)}`}>
              <div className="pt-hora-bar w-full rounded-t" data-franja={fr} style={{ height: `${Math.max((x.operaciones / max) * 100, x.operaciones > 0 ? 4 : 0)}%`, backgroundColor: FRANJA_COLOR[fr], opacity: fr === pico ? 1 : 0.45 }} />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[8px] text-slate-400 mt-0.5 px-0.5">
        <span>00h</span><span>06h</span><span>12h</span><span>18h</span><span>23h</span>
      </div>
      {pico && (
        <p className="text-xs text-slate-600 dark:text-slate-300 mt-2">
          Mayor actividad en la franja <strong style={{ color: FRANJA_COLOR[pico] }}>{pico}</strong> ({ah.franja_pico?.rango}) con el <strong>{ah.franja_pico?.pct}%</strong> de las operaciones · hora pico <strong>{String(hp.hora).padStart(2, '0')}:00 h</strong>.
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2" data-testid={`pt-franjas-${cuit}`}>
        {ah.franjas.map((fr) => (
          <div key={fr.nombre} className="rounded-lg px-2.5 py-1.5 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-1.5">
              <span className="pt-franja-dot h-2.5 w-2.5 rounded-full shrink-0" data-franja={fr.nombre} style={{ backgroundColor: FRANJA_COLOR[fr.nombre] }} />
              <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">{fr.nombre}</span>
              {fr.nombre === pico && <Badge className="bg-amber-100 text-amber-700 border-0 text-[8px] ml-auto">PICO</Badge>}
            </div>
            <p className="text-sm font-bold text-slate-800 dark:text-slate-100 mt-0.5">{fr.pct}%</p>
            <p className="text-[10px] text-slate-400">{fr.rango} · {fmtNum(fr.operaciones)} ops</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const PerfilCard = ({ p }) => {
  const cli = p.cliente; const res = p.resumen; const arca = p.arca || {}; const cr = p.credito || {};
  const apoc = arca.contrapartes_apocrifas || []; const vig = p.contrapartes_vigiladas || [];
  const nc = nivelColor(p.señales.nivel);
  return (
    <Card className="p-5 border-slate-200 dark:border-slate-800" data-testid={`pt-perfil-${cli.cuit}`}>
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">{cli.nombre || 'Sin nombre en el padrón'}</h3>
            {cli.tipo && <Badge className="bg-slate-100 text-slate-600 border-0">{cli.tipo}</Badge>}
            {cli.pep && <Badge className="bg-purple-100 text-purple-700 border-0">PEP</Badge>}
            {!cli.en_padron && <Badge className="bg-amber-100 text-amber-700 border-0">Fuera del padrón</Badge>}
          </div>
          <p className="font-mono text-xs text-slate-500 mt-0.5">{cli.cuit_fmt}{cli.fecha_alta ? ` · alta ${cli.fecha_alta}` : ''}{cli.cvus?.length ? ` · ${cli.cvus.length} CVU` : ''}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            <span className="text-slate-400">Actividad:</span> {arca.actividad_cliente || (arca.disponible ? '—' : 'sin datos ARCA')}
            {arca.monotributo ? ` · Monotributo ${arca.monotributo}` : ''}
          </p>
        </div>
        <div className="px-3 py-1.5 rounded-lg text-sm font-bold pt-riesgo-badge" data-nivel={p.señales.nivel} style={{ backgroundColor: nc.bg, color: nc.fg }} data-testid={`pt-riesgo-${cli.cuit}`}>
          Riesgo {p.señales.nivel}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mt-4">
        <KPI icon={TrendingUp} label="Ingresos" value={fmt(res.ingresos.monto)} sub={`${fmtNum(res.ingresos.operaciones)} ops`} tone="text-emerald-600" />
        <KPI icon={TrendingDown} label="Egresos" value={fmt(res.egresos.monto)} sub={`${fmtNum(res.egresos.operaciones)} ops`} tone="text-rose-600" />
        <KPI icon={ArrowRightLeft} label="Neto" value={fmt(res.neto)} sub={`${fmtNum(res.total_operaciones)} operaciones`} tone={res.neto >= 0 ? 'text-slate-800 dark:text-slate-100' : 'text-rose-600'} />
        <KPI icon={HandCoins} label="Ticket promedio" value={fmt(res.ticket_promedio)} sub={`Vol ${fmt(res.volumen_total)}`} />
      </div>

      {/* Datos ARCA (padrón ampliado A13) */}
      {(arca.disponible || (arca.impuestos && arca.impuestos.length) || (arca.domicilios && arca.domicilios.length)) && (
        <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3" data-testid={`pt-arca-${cli.cuit}`}>
          <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-1.5">
            <Building2 className="h-4 w-4" />Datos ARCA
            {arca.padron && <span className="text-[9px] uppercase bg-teal-100 text-teal-700 rounded px-1.5 py-0.5">Padrón {arca.padron}</span>}
          </p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
            <div><span className="text-slate-400">Condición:</span> {arca.condicion_iva ? arca.condicion_iva.replace(/_/g, ' ') : '—'}{arca.monotributo ? ` (Cat. ${arca.monotributo})` : ''}</div>
            {arca.email && <div className="truncate"><span className="text-slate-400">Email ARCA:</span> {arca.email}</div>}
            {arca.impuestos?.length > 0 && (
              <div className="sm:col-span-2">
                <span className="text-slate-400">Impuestos:</span>{' '}
                <span className="inline-flex flex-wrap gap-1 align-middle">
                  {arca.impuestos.map((im, i) => (
                    <span key={i} className="bg-slate-100 dark:bg-slate-800 rounded px-1.5 py-0.5 text-[10px]">{im.descripcion || im.id}{im.estado ? ` · ${im.estado}` : ''}</span>
                  ))}
                </span>
              </div>
            )}
            {arca.domicilios?.length > 0 && (
              <div className="sm:col-span-2">
                <span className="text-slate-400">Domicilios:</span>
                <ul className="mt-0.5 space-y-0.5">
                  {arca.domicilios.map((d, i) => (
                    <li key={i} className="text-slate-600 dark:text-slate-300">{d.tipo ? `[${d.tipo}] ` : ''}{d.direccion}{d.localidad ? `, ${d.localidad}` : ''}{d.provincia ? `, ${d.provincia}` : ''}{d.codigo_postal ? ` (CP ${d.codigo_postal})` : ''}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Serie + Señales */}
      <div className="grid md:grid-cols-2 gap-5 mt-5">
        <div>
          <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-1.5">Evolución mensual <span className="text-[10px] text-slate-400">(verde ingresos · rojo egresos)</span></p>
          <MiniBars serie={p.serie_mensual} />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-1.5 flex items-center gap-1.5"><ShieldAlert className="h-4 w-4" />Señales de riesgo (AML)</p>
          <div className="space-y-1.5" data-testid={`pt-senales-${cli.cuit}`}>
            {p.señales.items.map((s, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className="mt-0.5 h-2 w-2 rounded-full shrink-0 pt-senal-dot" data-nivel={s.nivel} style={{ backgroundColor: señalColor(s.nivel) }} />
                <span><strong className="text-slate-700 dark:text-slate-200">{s.tipo}:</strong> <span className="text-slate-500">{s.detalle}</span></span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contrapartes */}
      <div className="mt-5" data-testid={`pt-horario-${cli.cuit}`}>
        <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-1.5 flex items-center gap-1.5"><Clock className="h-4 w-4" />Franja horaria de operación <span className="text-[10px] text-slate-400 font-normal">(cuándo opera más)</span></p>
        <HoraBars ah={p.actividad_horaria} cuit={cli.cuit} />
      </div>

      {/* Contrapartes */}
      <div className="grid md:grid-cols-2 gap-5 mt-5">
        <Contrapartes titulo="Principales contrapartes · Ingresos (origen)" cps={p.contrapartes_ingreso} apoc={apoc} vig={vig} testid={`pt-cp-in-${cli.cuit}`} />
        <Contrapartes titulo="Principales contrapartes · Egresos (destino)" cps={p.contrapartes_egreso} apoc={apoc} vig={vig} testid={`pt-cp-out-${cli.cuit}`} />
      </div>

      {/* Crédito */}
      {cr.es_cliente_credito && (
        <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3 text-sm flex flex-wrap items-center gap-x-6 gap-y-1" data-testid={`pt-credito-${cli.cuit}`}>
          <span className="font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-1.5"><Building2 className="h-4 w-4" />Relación crediticia</span>
          <span className="text-slate-500">{cr.cantidad_prestamos} préstamo(s)</span>
          <span className="text-slate-500">Prestado <strong className="text-slate-700 dark:text-slate-200">{fmt(cr.total_prestado)}</strong></span>
          <span className="text-slate-500">Mora <strong className="text-rose-600">{fmt(cr.mora_total)}</strong> ({cr.prestamos_en_mora} en mora)</span>
        </div>
      )}
    </Card>
  );
};

export default function PerfilTransaccional() {
  const [modo, setModo] = useState('uno'); // uno | masivo
  const [cuit, setCuit] = useState('');
  const [cuitsTexto, setCuitsTexto] = useState('');
  const [desde, setDesde] = useState(hace12meses());
  const [hasta, setHasta] = useState(hoyISO());
  const [arca, setArca] = useState(true);
  const [loading, setLoading] = useState(false);
  const [perfiles, setPerfiles] = useState([]);
  const [meta, setMeta] = useState(null);
  const [exporting, setExporting] = useState('');
  const [tab, setTab] = useState('generar');
  const [historial, setHistorial] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histPerfil, setHistPerfil] = useState(null);
  const [histBusy, setHistBusy] = useState('');

  const cargarHistorial = useCallback(async () => {
    setHistLoading(true);
    try {
      const r = await api.get('/perfil-transaccional/historial');
      setHistorial(r.data.rows || []);
    } catch (e) {
      toast.error('No se pudo cargar el historial');
    } finally { setHistLoading(false); }
  }, []);

  const irAHistorial = () => { setTab('historial'); setHistPerfil(null); cargarHistorial(); };

  const verHist = async (c) => {
    setHistBusy(`ver-${c}`);
    try {
      const r = await api.get(`/perfil-transaccional/historial/${c}`);
      setHistPerfil(r.data.payload);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error('No se pudo abrir el perfil');
    } finally { setHistBusy(''); }
  };

  const exportarHist = async (c, formato) => {
    setHistBusy(`${formato}-${c}`);
    try {
      const r = await api.post('/perfil-transaccional/historial/export', { cuits: c, formato }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `perfil_${c}.${formato}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo exportar');
    } finally { setHistBusy(''); }
  };

  const quitarHist = async (c) => {
    try {
      await api.delete(`/perfil-transaccional/historial/${c}`);
      setHistorial((prev) => prev.filter((x) => x.cuit !== c));
      toast.success('Quitado del historial');
    } catch (e) { toast.error('No se pudo quitar'); }
  };

  const cuitsPayload = () => (modo === 'uno' ? cuit : cuitsTexto);

  const generar = useCallback(async () => {
    const payload = cuitsPayload();
    if (!payload || !payload.trim()) { toast.error('Ingresá al menos un CUIT'); return; }
    setLoading(true); setPerfiles([]); setMeta(null);
    try {
      const r = await api.post('/perfil-transaccional/generar', { cuits: payload, desde, hasta, arca });
      setPerfiles(r.data.perfiles || []);
      setMeta({ total: r.data.total, invalidos: r.data.invalidos || [], errores: r.data.errores || [] });
      if ((r.data.perfiles || []).length === 0) toast.info('No se generó ningún perfil');
      else toast.success(`${r.data.perfiles.length} perfil(es) generado(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo generar el perfil');
    } finally { setLoading(false); }
  }, [modo, cuit, cuitsTexto, desde, hasta, arca]);

  const exportar = async (formato) => {
    const payload = cuitsPayload();
    if (!payload || !payload.trim()) { toast.error('Ingresá al menos un CUIT'); return; }
    setExporting(formato);
    try {
      const r = await api.post('/perfil-transaccional/export', { cuits: payload, desde, hasta, arca, formato }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `perfil_transaccional.${formato}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo exportar');
    } finally { setExporting(''); }
  };

  return (
    <div className="p-6 lg:p-8 pt-20 sm:pt-6 lg:pt-8 max-w-6xl mx-auto" data-testid="perfil-transaccional-page">
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2.5 rounded-xl bg-teal-500/10"><UserSearch className="h-6 w-6 text-teal-600" /></div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Perfil Transaccional</h1>
          <p className="text-sm text-slate-500">Operatoria del cliente cruzada con el padrón, ARCA y la cartera de préstamos</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5">
        <button onClick={() => setTab('generar')} data-testid="pt-tab-generar" className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${tab === 'generar' ? 'bg-teal-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600'}`}><UserSearch className="h-4 w-4" />Generar</button>
        <button onClick={irAHistorial} data-testid="pt-tab-historial" className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${tab === 'historial' ? 'bg-teal-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600'}`}><History className="h-4 w-4" />Historial</button>
      </div>

      {tab === 'generar' && (
      <>
      {/* Controles */}
      <Card className="p-4 border-slate-200 dark:border-slate-800 mb-5">
        <div className="flex gap-2 mb-3">
          <button onClick={() => setModo('uno')} data-testid="pt-modo-uno" className={`px-3 py-1.5 rounded-lg text-sm font-medium ${modo === 'uno' ? 'bg-teal-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600'}`}>Un cliente</button>
          <button onClick={() => setModo('masivo')} data-testid="pt-modo-masivo" className={`px-3 py-1.5 rounded-lg text-sm font-medium ${modo === 'masivo' ? 'bg-teal-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600'}`}>Varios (lista)</button>
        </div>

        {modo === 'uno' ? (
          <Input value={cuit} onChange={(e) => setCuit(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && generar()} placeholder="CUIT del cliente (ej. 27-32506991-6)" className="font-mono" data-testid="pt-cuit-input" />
        ) : (
          <Textarea value={cuitsTexto} onChange={(e) => setCuitsTexto(e.target.value)} placeholder="Pegá una lista de CUITs (uno por línea o separados por coma). Máx. 25." rows={4} className="font-mono text-sm" data-testid="pt-cuits-textarea" />
        )}

        <div className="flex flex-wrap items-end gap-3 mt-3">
          <div><label className="text-[11px] text-slate-500 block mb-1">Desde</label><Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9 w-40" data-testid="pt-desde" /></div>
          <div><label className="text-[11px] text-slate-500 block mb-1">Hasta</label><Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9 w-40" data-testid="pt-hasta" /></div>
          <div className="flex items-center gap-2 pb-1.5">
            <Switch checked={arca} onCheckedChange={setArca} data-testid="pt-arca-toggle" />
            <span className="text-sm text-slate-600 dark:text-slate-300">Cruzar con ARCA (actividad + apócrifos)</span>
          </div>
          <div className="flex-1" />
          <Button onClick={generar} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white h-9" data-testid="pt-generar-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UserSearch className="h-4 w-4 mr-1.5" />}Generar
          </Button>
          <Button variant="outline" onClick={() => exportar('pdf')} disabled={!!exporting} className="h-9" data-testid="pt-export-pdf">
            {exporting === 'pdf' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />}PDF
          </Button>
          <Button variant="outline" onClick={() => exportar('xlsx')} disabled={!!exporting} className="h-9" data-testid="pt-export-xlsx">
            {exporting === 'xlsx' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />}Excel
          </Button>
        </div>
        {arca && <p className="text-[11px] text-amber-600 mt-2">El cruce con ARCA requiere el certificado cargado (producción). En preview puede figurar "sin datos ARCA".</p>}
      </Card>

      {/* Resultados */}
      {meta && (meta.invalidos.length > 0 || meta.errores.length > 0) && (
        <div className="mb-3 text-xs text-amber-600 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />
          {meta.invalidos.length > 0 && <span>Ignorados (CUIT inválido): {meta.invalidos.join(', ')}. </span>}
          {meta.errores.length > 0 && <span>Con error: {meta.errores.map((e) => e.cuit).join(', ')}.</span>}
        </div>
      )}

      {loading && <div className="py-16 text-center"><Loader2 className="h-7 w-7 animate-spin mx-auto text-teal-500" /><p className="text-sm text-slate-400 mt-2">Analizando operaciones…</p></div>}

      {!loading && perfiles.length === 0 && !meta && (
        <div className="py-16 text-center text-slate-400" data-testid="pt-empty">
          <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
          Ingresá uno o varios CUITs y generá el perfil transaccional.
        </div>
      )}

      <div className="space-y-5">
        {perfiles.map((p) => <PerfilCard key={p.cliente.cuit} p={p} />)}
      </div>
      </>
      )}

      {tab === 'historial' && (
        <div data-testid="pt-historial">
          {histPerfil ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-500">Perfil guardado (sin regenerar)</p>
                <Button variant="outline" size="sm" onClick={() => setHistPerfil(null)} data-testid="pt-hist-volver">← Volver al historial</Button>
              </div>
              <PerfilCard p={histPerfil} />
            </div>
          ) : (
            <Card className="p-4 border-slate-200 dark:border-slate-800">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1.5"><History className="h-4 w-4" />Perfiles generados recientemente <span className="text-xs text-slate-400 font-normal">(últimos 50 · compartido)</span></p>
                <Button variant="ghost" size="sm" onClick={cargarHistorial} data-testid="pt-hist-refresh"><RefreshCw className="h-4 w-4" /></Button>
              </div>
              {histLoading ? (
                <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-teal-500" /></div>
              ) : historial.length === 0 ? (
                <div className="py-12 text-center text-slate-400"><History className="h-8 w-8 mx-auto mb-2 opacity-40" />Todavía no se generó ningún perfil.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-slate-400 text-left border-b border-slate-100 dark:border-slate-800 text-xs">
                      <th className="py-2 pr-3">Cliente</th><th className="py-2 pr-3">CUIT</th><th className="py-2 pr-3">Riesgo</th>
                      <th className="py-2 pr-3 text-right">Ingresos</th><th className="py-2 pr-3 text-right">Egresos</th><th className="py-2 pr-3 text-right">Ops.</th>
                      <th className="py-2 pr-3">Generado</th><th className="py-2 text-right">Acciones</th>
                    </tr></thead>
                    <tbody>
                      {historial.map((h) => {
                        const nc = nivelColor(h.riesgo);
                        return (
                          <tr key={h.cuit} className="border-b border-slate-50 dark:border-slate-800/60 hover:bg-slate-50/60 dark:hover:bg-slate-800/40" data-testid={`pt-hist-row-${h.cuit}`}>
                            <td className="py-2 pr-3 font-medium text-slate-800 dark:text-slate-100 max-w-[200px] truncate">{h.nombre || '—'}<div className="text-[10px] text-slate-400 font-normal">{h.tipo}{h.en_padron === false ? ' · fuera del padrón' : ''}</div></td>
                            <td className="py-2 pr-3 font-mono text-xs text-slate-500">{h.cuit_fmt}</td>
                            <td className="py-2 pr-3"><span className="text-[11px] font-bold px-2 py-0.5 rounded-full pt-riesgo-badge" data-nivel={h.riesgo} style={{ backgroundColor: nc.bg, color: nc.fg }}>{h.riesgo}</span></td>
                            <td className="py-2 pr-3 text-right text-emerald-600 font-semibold">{fmt(h.ingresos)}</td>
                            <td className="py-2 pr-3 text-right text-rose-600 font-semibold">{fmt(h.egresos)}</td>
                            <td className="py-2 pr-3 text-right text-slate-600 dark:text-slate-300">{fmtNum(h.operaciones)}</td>
                            <td className="py-2 pr-3 text-xs text-slate-400">{(h.generado_por_nombre || h.generado_por || '').split('@')[0] || '—'}<div>{(h.generado_at || '').slice(0, 10)}</div></td>
                            <td className="py-2 text-right whitespace-nowrap">
                              <Button variant="ghost" size="sm" className="h-7 px-2" title="Ver" onClick={() => verHist(h.cuit)} disabled={histBusy === `ver-${h.cuit}`} data-testid={`pt-hist-ver-${h.cuit}`}>{histBusy === `ver-${h.cuit}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}</Button>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-rose-600" title="PDF" onClick={() => exportarHist(h.cuit, 'pdf')} disabled={histBusy === `pdf-${h.cuit}`} data-testid={`pt-hist-pdf-${h.cuit}`}>{histBusy === `pdf-${h.cuit}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}</Button>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-emerald-700" title="Excel" onClick={() => exportarHist(h.cuit, 'xlsx')} disabled={histBusy === `xlsx-${h.cuit}`} data-testid={`pt-hist-xlsx-${h.cuit}`}>{histBusy === `xlsx-${h.cuit}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}</Button>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-400 hover:text-rose-600" title="Quitar" onClick={() => quitarHist(h.cuit)} data-testid={`pt-hist-quitar-${h.cuit}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
